import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Globe, Mail, Phone, MapPin, Newspaper, ShoppingBag, MessageCircle } from "lucide-react";

export default function PublicWebsite() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [articles, setArticles] = useState([]);
  const [products, setProducts] = useState([]);
  const [epapers, setEpapers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/websites/public/${slug}`);
        setW(r.data);
        if (r.data.type === "news" || r.data.type === "blog") {
          const a = await api.get(`/articles?website_id=${r.data.website_id}&status=published`);
          setArticles(a.data);
          const ep = await api.get(`/epapers?website_id=${r.data.website_id}`);
          setEpapers(ep.data);
        }
        if (r.data.type === "ecommerce" && r.data.store_id) {
          // redirect to storefront
          navigate(`/s/${r.data.slug}`);
          return;
        }
      } catch { navigate("/"); }
    })();
  }, [slug, navigate]);

  if (!w) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"/></div>;

  const brand = w.primary_color || "#0EA5E9";
  const style = { "--brand": brand };

  return (
    <div className="min-h-screen bg-white" style={style}>
      {w.type === "news" || w.type === "blog" ? (
        <NewsSite w={w} articles={articles} epapers={epapers}/>
      ) : (
        <GenericSite w={w}/>
      )}
    </div>
  );
}

function GenericSite({ w }) {
  const brand = w.primary_color;
  return (
    <>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {w.logo_url ? <img src={fileUrl(w.logo_url)} className="w-9 h-9 rounded" alt=""/> : <div className="w-9 h-9 rounded-lg text-white font-black flex items-center justify-center" style={{ background: brand }}>{w.name[0]}</div>}
            <span className="font-extrabold">{w.name}</span>
          </Link>
        </div>
      </header>
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand}, #0F172A)` }}>
        <div className="max-w-7xl mx-auto px-4 py-24 text-center text-white">
          <h1 className="text-4xl sm:text-6xl font-extrabold font-bn">{w.content?.hero_title || w.name}</h1>
          <p className="mt-4 text-xl opacity-90 font-bn max-w-2xl mx-auto">{w.content?.hero_subtitle || "Welcome to our website"}</p>
        </div>
      </section>
      {w.content?.about && (
        <section className="py-20 max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-extrabold">About</h2>
          <p className="mt-4 text-slate-600 whitespace-pre-line font-bn">{w.content.about}</p>
        </section>
      )}
      <section className="bg-slate-50 py-20">
        <div className="max-w-4xl mx-auto px-4 grid md:grid-cols-3 gap-6 text-center">
          {w.content?.email && <div className="p-6 bg-white rounded-xl"><Mail className="w-6 h-6 mx-auto mb-2" style={{ color: brand }}/><div className="font-bold">Email</div><div className="text-sm text-slate-600">{w.content.email}</div></div>}
          {w.content?.phone && <div className="p-6 bg-white rounded-xl"><Phone className="w-6 h-6 mx-auto mb-2" style={{ color: brand }}/><div className="font-bold">Phone</div><div className="text-sm text-slate-600">{w.content.phone}</div></div>}
          {w.content?.address && <div className="p-6 bg-white rounded-xl"><MapPin className="w-6 h-6 mx-auto mb-2" style={{ color: brand }}/><div className="font-bold">Address</div><div className="text-sm text-slate-600 font-bn">{w.content.address}</div></div>}
        </div>
      </section>
      <footer className="py-8 text-center text-sm text-slate-500 border-t">
        © 2026 {w.name} · Powered by <Link to="/" className="text-sky-600 font-semibold">OxyCom</Link>
      </footer>
    </>
  );
}

function NewsSite({ w, articles, epapers }) {
  const brand = w.primary_color;
  const categories = [...new Set(articles.map(a => a.category))];
  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <>
      <div className="text-white text-xs py-1.5 text-center font-bn" style={{ background: "#0F172A" }}>
        📰 {new Date().toLocaleDateString("bn-BD", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
      </div>
      <header className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-4xl font-black font-bn" style={{ color: brand }}>{w.name}</h1>
          </Link>
        </div>
        <div className="border-t">
          <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-4 overflow-x-auto no-scrollbar text-sm font-semibold">
            <a href="#" className="text-slate-700 whitespace-nowrap hover:text-rose-600">সর্বশেষ</a>
            {categories.map(c => <a key={c} href={`#cat-${c}`} className="text-slate-600 whitespace-nowrap hover:text-rose-600">{c}</a>)}
            {epapers.length > 0 && <a href="#epaper" className="text-rose-600 font-bold whitespace-nowrap"><Newspaper className="w-4 h-4 inline mr-1"/>ই-পেপার</a>}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {articles.length === 0 ? (
          <div className="text-center py-20 text-slate-400">No articles published yet</div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link to={`/w/${w.slug}/article/${featured.article_id}`} className="grid md:grid-cols-2 gap-6 mb-8 group">
                <img src={fileUrl(featured.featured_image) || "https://placehold.co/800x600"} className="w-full aspect-video rounded-xl object-cover" alt=""/>
                <div>
                  <div className="text-xs font-black uppercase text-rose-600">{featured.category}</div>
                  <h2 className="text-3xl font-black mt-2 group-hover:text-rose-600 font-bn">{featured.title}</h2>
                  <p className="mt-3 text-slate-600 font-bn">{featured.excerpt}</p>
                  <div className="text-xs text-slate-400 mt-3">By {featured.author}</div>
                </div>
              </Link>
            )}

            {/* Rest grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map(a => (
                <Link key={a.article_id} to={`/w/${w.slug}/article/${a.article_id}`} className="group" data-testid={`pub-article-${a.article_id}`}>
                  <img src={fileUrl(a.featured_image) || "https://placehold.co/400x300"} className="w-full aspect-video rounded-lg object-cover" alt=""/>
                  <div className="mt-2 text-[10px] font-black uppercase text-rose-600">{a.category}</div>
                  <div className="text-lg font-bold group-hover:text-rose-600 font-bn line-clamp-2">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2 font-bn">{a.excerpt}</div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* E-Paper */}
        {epapers.length > 0 && (
          <section id="epaper" className="mt-14 pt-8 border-t">
            <h3 className="text-2xl font-black font-bn">ই-পেপার · E-Paper</h3>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {epapers.map(ep => (
                <div key={ep.epaper_id} className="border rounded-lg overflow-hidden">
                  <div className="aspect-[3/4] bg-slate-100">
                    {ep.pages?.[0] && <img src={fileUrl(ep.pages[0])} className="w-full h-full object-cover" alt=""/>}
                  </div>
                  <div className="p-2 text-xs">
                    <div className="font-bold truncate font-bn">{ep.title}</div>
                    <div className="text-[10px] text-slate-500">{ep.edition_date}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="border-t py-8 text-center text-sm text-slate-500">
        © 2026 {w.name} · <Link to="/" className="text-rose-600 font-semibold">OxyCom</Link>
      </footer>
    </>
  );
}
